/**
 * Foco e anúncios da sessão da plataforma (#430 / UX #434).
 * A live region vive no Shell (fora da faixa); o texto só muda depois de
 * ela já estar no DOM. O foco no h1 espera a rota nova montar — e, ao
 * entrar, o h1 pode aparecer só depois do Spinner (MutationObserver).
 */

const FOCUS_H1_TIMEOUT_MS = 3_000

let focusPending = false
let focusObserver: MutationObserver | null = null
let focusTimeoutId: ReturnType<typeof setTimeout> | null = null

/** Marca que o próximo caminho montado deve receber foco no h1. */
export function requestFocusPageH1(): void {
  focusPending = true
}

export function isFocusPageH1Pending(): boolean {
  return focusPending
}

function clearFocusWait(): void {
  focusObserver?.disconnect()
  focusObserver = null
  if (focusTimeoutId !== null) {
    clearTimeout(focusTimeoutId)
    focusTimeoutId = null
  }
}

function tryFocusH1(root: ParentNode): boolean {
  const h1 = root.querySelector('main h1, h1')
  if (!(h1 instanceof HTMLElement)) return false
  if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1')
  h1.focus()
  focusPending = false
  clearFocusWait()
  return true
}

/**
 * Se houver foco pendente, tenta focar o h1. Se ainda não existir (ex.:
 * Spinner do Dashboard), mantém o pedido e observa o DOM até 3 s
 * (MutationObserver + polling — o polling cobre ambientes sem MO fiável).
 * Chamar num useEffect que depende da rota.
 */
export function flushPendingFocusPageH1(root: ParentNode = document): boolean {
  if (!focusPending) return false
  if (tryFocusH1(root)) return true

  clearFocusWait()
  const startedAt = Date.now()
  const observeTarget = root instanceof Document ? root.body : root instanceof Element ? root : null
  if (observeTarget && typeof MutationObserver !== 'undefined') {
    focusObserver = new MutationObserver(() => {
      tryFocusH1(root)
    })
    focusObserver.observe(observeTarget, { childList: true, subtree: true })
  }
  const poll = () => {
    if (!focusPending) return
    if (tryFocusH1(root)) return
    if (Date.now() - startedAt >= FOCUS_H1_TIMEOUT_MS) {
      focusPending = false
      clearFocusWait()
      return
    }
    focusTimeoutId = setTimeout(poll, 50)
  }
  focusTimeoutId = setTimeout(poll, 50)
  return false
}

/** @internal testes */
export function resetFocusPageH1Pending(): void {
  focusPending = false
  clearFocusWait()
}

type AnnounceListener = (message: string) => void

const announceListeners = new Set<AnnounceListener>()
let lastAnnouncement = ''

/** Assina a live region persistente (ImpersonationAnnouncer). */
export function onImpersonationAnnounce(fn: AnnounceListener): () => void {
  announceListeners.add(fn)
  if (lastAnnouncement) fn(lastAnnouncement)
  return () => {
    announceListeners.delete(fn)
  }
}

/**
 * Escreve na live region já montada. Limpa e re-põe o texto no microtask
 * seguinte para leitores de ecrã detetarem a mudança (mesmo texto de novo).
 */
export function announceImpersonation(message: string): void {
  lastAnnouncement = message
  for (const fn of announceListeners) fn('')
  queueMicrotask(() => {
    for (const fn of announceListeners) fn(message)
  })
}

/** @internal testes */
export function getLastImpersonationAnnouncement(): string {
  return lastAnnouncement
}

/** @internal testes */
export function resetImpersonationAnnouncement(): void {
  lastAnnouncement = ''
  for (const fn of announceListeners) fn('')
}

/** Duração curta com Intl (ex.: «12 min» / «12 min.» conforme o locale). */
export function formatDurationMinutes(mins: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'minute',
    unitDisplay: 'short',
  }).format(mins)
}
